import { useCallback, useEffect, useRef, useState } from 'react';
import { OSCAR_PANEL_MAX_WIDTH } from '../constants/AIAgentConstants';

/** Figma 14998:125299 — the designed column width, and the narrowest allowed. */
export const OSCAR_PANEL_MIN_WIDTH = 420;

/** Leave enough of the app visible that the panel never becomes the whole screen. */
const MIN_VISIBLE_APP_WIDTH = 320;

/** Horizontal padding on the panel shell (`px-xs`), outside the resizable column. */
const PANEL_SHELL_PADDING = 16;

const WIDTH_KEY = 'oscar.panelWidth';

/** Arrow-key step, so the handle is usable without a pointer. */
const KEYBOARD_STEP = 24;

const clampWidth = (width: number): number => {
	const max = Math.max(
		OSCAR_PANEL_MIN_WIDTH,
		Math.min(OSCAR_PANEL_MAX_WIDTH, window.innerWidth - MIN_VISIBLE_APP_WIDTH)
	);
	return Math.round(Math.min(max, Math.max(OSCAR_PANEL_MIN_WIDTH, width)));
};

const readStoredWidth = (): number => {
	try {
		const raw = window.localStorage.getItem(WIDTH_KEY);
		const stored = raw ? Number(raw) : NaN;
		// `clampWidth(NaN)` is NaN — `Math.max`/`Math.min` propagate it — and it
		// would be written straight back on the next commit, so one unreadable
		// value would stick as `width: NaNpx` until storage was cleared by hand.
		return isFinite(stored) ? clampWidth(stored) : OSCAR_PANEL_MIN_WIDTH;
	} catch (storageError) {
		return OSCAR_PANEL_MIN_WIDTH;
	}
};

const storeWidth = (width: number): void => {
	try {
		window.localStorage.setItem(WIDTH_KEY, String(width));
	} catch (storageError) {
		// A width preference is not worth failing over.
	}
};

/**
 * Drag-to-resize for the docked panel.
 *
 * The width is written straight to the element during a drag and only committed
 * to state on release. Re-rendering on every pointer move would re-render the
 * whole transcript — markdown, tables and all — which stutters badly once a
 * conversation is long.
 *
 * Persisted in `localStorage`: it is a display preference, not conversation
 * data, so unlike the conversation id it is not scoped to an account.
 */
export const useOscarPanelWidth = () => {
	const [width, setWidth] = useState(readStoredWidth);
	const columnRef = useRef<HTMLDivElement>(null);
	const widthRef = useRef(width);

	const applyWidth = useCallback((next: number) => {
		widthRef.current = next;
		if (columnRef.current) {
			columnRef.current.style.width = `${next}px`;
		}
	}, []);

	const commitWidth = useCallback(() => {
		setWidth(widthRef.current);
		storeWidth(widthRef.current);
	}, []);

	const handlePointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			event.preventDefault();
			const handle = event.currentTarget;
			handle.setPointerCapture(event.pointerId);
			// The panel is docked right, so width grows as the pointer moves left.
			const onMove = (moveEvent: PointerEvent) => {
				applyWidth(clampWidth(window.innerWidth - moveEvent.clientX - PANEL_SHELL_PADDING));
			};
			// One finaliser for every way a drag can end. `pointerup` is not the only
			// one: a touch gesture, a window focus change or unmounting mid-drag
			// raises `pointercancel` or `lostpointercapture` instead, and without
			// these the body kept `user-select: none` and text stayed unselectable
			// across the whole app until a reload.
			const finish = (endEvent: PointerEvent) => {
				if (handle.hasPointerCapture(endEvent.pointerId)) {
					handle.releasePointerCapture(endEvent.pointerId);
				}
				handle.removeEventListener('pointermove', onMove);
				handle.removeEventListener('pointerup', finish);
				handle.removeEventListener('pointercancel', finish);
				handle.removeEventListener('lostpointercapture', finish);
				document.body.style.removeProperty('user-select');
				commitWidth();
			};
			// Without this the drag selects transcript text as it passes over it.
			document.body.style.setProperty('user-select', 'none');
			handle.addEventListener('pointermove', onMove);
			handle.addEventListener('pointerup', finish);
			handle.addEventListener('pointercancel', finish);
			handle.addEventListener('lostpointercapture', finish);
		},
		[applyWidth, commitWidth]
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
				return;
			}
			event.preventDefault();
			const delta = event.key === 'ArrowLeft' ? KEYBOARD_STEP : -KEYBOARD_STEP;
			applyWidth(clampWidth(widthRef.current + delta));
			commitWidth();
		},
		[applyWidth, commitWidth]
	);

	// The maximum is derived from the viewport, so it moves. Without this a panel
	// dragged wide on a large display kept that width through a window snap or a
	// rotation and covered the app it is meant to sit beside — the guarantee
	// `MIN_VISIBLE_APP_WIDTH` exists to make only held until the window changed.
	useEffect(() => {
		const onResize = () => {
			const clamped = clampWidth(widthRef.current);
			if (clamped !== widthRef.current) {
				applyWidth(clamped);
				commitWidth();
			}
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, [applyWidth, commitWidth]);

	return { width, columnRef, handlePointerDown, handleKeyDown };
};

export default useOscarPanelWidth;
